/**
 * planetTerrainClient — main-thread orchestrator for procedural planet
 * texture baking. Three-layer caching:
 *
 *   L1  in-memory Map<seed:diameter, Texture>   — sync, instant for same tab
 *   L2  IndexedDB Blob<seed:diameter>            — async, persists across reloads
 *   L0  Web Worker bake                          — last resort, off main thread
 *
 * Pending-request dedupe: two Planet instances asking for the same seed in
 * the same tick share one bake. Worker doesn't need its own cache.
 *
 * If workers/IDB/OffscreenCanvas aren't available (very old browsers), falls
 * back to the previous synchronous main-thread path so the game still works.
 */

import { Texture } from 'pixi.js';
import { readTerrainBlob, writeTerrainBlob } from './planetTerrainIDB';

export interface PlanetTextureRequest {
  seed: number;
  density: number;
  planetType: string;
  diameter: number; // sprite diameter in pixels (capped to 512 inside the worker)
  /** size_multiplier from the planet profile — picks G(6)/G(8)/G(10) mesh. */
  sizeMultiplier: number;
}

const memCache: Map<string, Texture> = new Map();
const pending: Map<string, Promise<Texture>> = new Map();

let worker: Worker | null = null;
let nextRequestId = 1;
const inflight: Map<number, { resolve: (v: WorkerResult) => void; reject: (e: Error) => void }> = new Map();

interface WorkerResult {
  bitmap: ImageBitmap;
  blob: Blob;
}

interface WorkerSuccessMsg {
  id: number;
  bitmap: ImageBitmap;
  blob: Blob;
}
interface WorkerErrorMsg {
  id: number;
  error: string;
}

function cacheKey(seed: number, diameter: number): string {
  // `hex2` invalidates the single-mesh hex bake — multi-res planets bake
  // different content for the same seed depending on the planet's frequency.
  return `hex2:${seed}:${Math.min(diameter, 512)}`;
}

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return null;
  try {
    worker = new Worker(new URL('./planetTerrainWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerSuccessMsg | WorkerErrorMsg>) => {
      const msg = e.data;
      const slot = inflight.get(msg.id);
      if (!slot) return;
      inflight.delete(msg.id);
      if ('error' in msg) {
        slot.reject(new Error(msg.error));
      } else {
        slot.resolve({ bitmap: msg.bitmap, blob: msg.blob });
      }
    };
    worker.onerror = (e) => {
      // Reject everything in flight — caller will fall back to sync path.
      for (const { reject } of inflight.values()) reject(new Error(e.message || 'worker error'));
      inflight.clear();
    };
  } catch {
    worker = null;
  }
  return worker;
}

function bakeInWorker(req: PlanetTextureRequest): Promise<WorkerResult> {
  const w = ensureWorker();
  if (!w) return Promise.reject(new Error('worker unavailable'));
  const id = nextRequestId++;
  return new Promise<WorkerResult>((resolve, reject) => {
    inflight.set(id, { resolve, reject });
    w.postMessage({
      id,
      seed: req.seed,
      density: req.density,
      planetType: req.planetType,
      diameter: req.diameter,
      sizeMultiplier: req.sizeMultiplier,
    });
  });
}

/** Synchronous L1 lookup. Returns null on miss. */
export function getCachedPlanetTexture(seed: number, diameter: number): Texture | null {
  return memCache.get(cacheKey(seed, diameter)) ?? null;
}

/** Get-or-bake. Resolves with a Pixi Texture. Never rejects — falls back to
 * the synchronous main-thread bake if both worker and IDB are unavailable. */
export async function getPlanetTexture(req: PlanetTextureRequest): Promise<Texture> {
  const key = cacheKey(req.seed, req.diameter);

  const cached = memCache.get(key);
  if (cached) return cached;

  const inFlight = pending.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<Texture> => {
    // L2: IndexedDB lookup
    try {
      const blob = await readTerrainBlob(req.seed, Math.min(req.diameter, 512));
      if (blob) {
        const bitmap = await createImageBitmap(blob);
        const texture = Texture.from(bitmap);
        memCache.set(key, texture);
        return texture;
      }
    } catch {
      // Ignore — fall through to worker bake
    }

    // L0: Worker bake
    try {
      const { bitmap, blob } = await bakeInWorker(req);
      const texture = Texture.from(bitmap);
      memCache.set(key, texture);
      // Fire-and-forget write to L2
      void writeTerrainBlob(req.seed, Math.min(req.diameter, 512), blob);
      return texture;
    } catch {
      // Final fallback: synchronous main-thread bake. Imported lazily so the
      // worker chunk is the default code path and we don't pay this import
      // unless something failed.
      const { bakeCircularPlanetTextureSync } = await import('./planetTerrainSync');
      const texture = bakeCircularPlanetTextureSync(req);
      memCache.set(key, texture);
      return texture;
    }
  })();

  pending.set(key, promise);
  promise.finally(() => pending.delete(key));
  return promise;
}
